import React from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import IntlComponent from 'axway-react-intl';
import { Form, Panel, Status } from 'axway-react-ui';
import { FormControl, Tabs } from 'react-bootstrap';
import { jsonSchemaToJSONPath } from 'components/utility/jsonpath';
import FlowPropTypes from '../PropTypes';
import Property from './Property';
import Description from './Description';
import Title from './Title';
import renderParametersTab from './renderParametersTab';
import renderTriggerOutputsTab from './renderTriggerOutputsTab';
import renderTriggerInputsTab from './renderTriggerInputsTab';

function isJSONPath(value) {
	return (value === '$' || value.startsWith('$.'));
}
function getValueType(value) {
	return isJSONPath(value) ? 'jsonpath' : 'string';
}

/**
 * Converts a JSON schema and converts them to flow-node `inputParameters`.
 * see: flownodeschema-v2.json#/definitions/inputParameters.
 * @param {object} schema - A JSON schema.
 * @param {object} inputParameters - An empty `inputParameters` definition
 * that will be populated.
 * @param {object} [additionalProperties={}] - Additional properties to be
 * added to the `inputParameters` when converting (e.g. group, options).
 */
function schemaToInputParameters(schema, inputParameters, additionalProperties = {}) {
	if (!schema) {
		return;
	}
	for (const name in schema.properties) {
		const prop = schema.properties[name];

		if (inputParameters.properties.hasOwnProperty(name)) {
			// eslint-disable-next-line no-console
			console.warn(
				`property '${name}'`, prop, 'clashes with existing',
				inputParameters.properties[name]
			);
			continue;
		}

		inputParameters.properties[name] = {
			...prop,
			options: {
				initialType: 'string'
			},
			...additionalProperties
		};

		if (schema.required && schema.required.includes(name)) {
			inputParameters.required.push(name);
		}
	}
}

/**
 * Converts a key/value map of input parameters to an array of flow parameter.
 * see: flowschema-v4.json#/definitions/parameters
 * @param {object} obj - An object of key/values.
 * @param {object} valueTypes - An object of key:values mapping paramId to
 * its valueType (i.e. jsonpath|string).
 * @param {object[]} flowParameters - An empty array to hold the converted
 * parameters.
 */
function mapToFlowParameters(obj, valueTypes, flowParameters) {
	for (const name in obj) {
		const existing = flowParameters.find(a => a.name === name);
		if (existing) {
			// eslint-disable-next-line no-console
			console.warn(`param '${name}'`, 'clashes with existing', existing);
			continue;
		}
		const valueType = valueTypes[name];
		const value = (valueType === 'jsonpath')
			? obj[name] : JSON.stringify(obj[name]);
		flowParameters.push({
			name,
			type: valueType,
			value
		});
	}
}

/**
 * Renders trigger configuration panel.
 */
class TriggerConfig extends React.Component {
	constructor(props) {
		super(props);
		IntlComponent.init(this);

		this.onEnableParameter = this.onEnableParameter.bind(this);
		this.onEnableInputParameter = this.onEnableInputParameter.bind(this);
		this.onDeleteParameter = this.onDeleteParameter.bind(this);
		this.onChangeParameter = this.onChangeParameter.bind(this);
		this.onChangeInputParameter = this.onChangeInputParameter.bind(this);
		this.onChangeNodeName = this.onChangeNodeName.bind(this);

		this.configureParameterValueTypes(props);
	}

	componentWillReceiveProps(nextProps) {
		// changed trigger
		if (nextProps.triggerId !== this.props.triggerId) {
			this.configureParameterValueTypes(nextProps);
		}
	}

	configureParameterValueTypes(props) {
		// There are a couple of reasons for these `valueTypes`.  First
		// is that we do not persist the `valueType`, so this caches it
		// so that we know how to handle the `value` when it changes.
		// Second is that parameters can clash with triggers (even though
		// they currently do not), so these need to be kept separate.
		const { trigger } = props;
		const valueTypes = {
			triggerParameters: {},
			channelParameters: {}
		};
		const values = {
			triggerParameters: {},
			channelParameters: {}
		};
		for (const paramId in trigger.triggerParameters) {
			const value = trigger.triggerParameters[paramId];
			const valueType = getValueType(value);
			valueTypes.triggerParameters[paramId] = valueType;
			values.triggerParameters[paramId] = value;
		}
		for (const paramId in trigger.channelParameters) {
			const value = trigger.channelParameters[paramId];
			const valueType = getValueType(value);
			valueTypes.channelParameters[paramId] = valueType;
			values.channelParameters[paramId] = value;
		}
		this.parameters = {
			valueTypes,
			values
		};
	}

	renderChannel() {
		const { trigger, definition = {} } = this.props;
		const { channelId } = trigger;
		if (!channelId) {
			return null;
		}
		const { channelFriendlyName = 'Channel' } = definition;
		const label = `${channelFriendlyName} ID`;
		return (
			<Property>
				<Property.Name>
					{label}
				</Property.Name>
				<Property.Value>
					<FormControl.Static className="nodeconfig-name">
						{channelId}
					</FormControl.Static>
				</Property.Value>
			</Property>
		);
	}

	renderDescription() {
		const { definition } = this.props;
		if (!definition) {
			// unregistered plugin
			return null;
		}
		const { description } = definition;
		return (
			<Description>
				{description}
			</Description>
		);
	}

	/**
	 * Method to change the trigger's node name property.
	 *
	 * @param  {Object} data - The new name to change to.
	 */
	onChangeNodeName(data) {
		if (!data || !data.name) {
			return;
		}
		const { onChange, triggerId, triggerType } = this.props;
		onChange && onChange({
			triggerId,
			triggerType,
			type: 'name',
			newValue: data.name
		});
	}

	onDeleteParameter(paramId, group) {
		this.onEnableParameter(paramId, false, 'string', null, group);
	}

	onEnableParameter(paramId, enabled, valueType, value, group) {
		const { onParamEnable, triggerId, triggerType } = this.props;
		const { valueTypes } = this.parameters;
		const paramType = (group)
			? 'channelParameters' : 'triggerParameters';
		const type = (group)
			? 'channelParameter' : 'triggerParameter';

		valueTypes[paramType][paramId] = valueType;

		onParamEnable({
			triggerId,
			triggerType,
			paramId,
			type,
			enabled,
			valueType,
			value
		});
	}

	onEnableInputParameter(paramId, enabled, valueType, value) {
		const { onParamEnable, triggerId, triggerType } = this.props;
		onParamEnable({
			triggerId,
			triggerType,
			paramId,
			type: 'inputParameter',
			enabled,
			valueType,
			value
		});
	}

	onChangeParameter(paramId, key, value, group) {
		const { trigger, onChange, triggerId, triggerType } = this.props;
		const { valueTypes, values } = this.parameters;
		const paramType = (group)
			? 'channelParameters' : 'triggerParameters';

		// A note about this - each NodeConfigField change causes two events
		// with keys: "value" and "valueType", in that order.  As this is a
		// view of the trigger config, we really shouldn't be mucking with
		// that in memory.
		let newValue;
		// istanbul ignore else
		if (key === 'value') {
			// First event - change of value.  The current valueType is
			// retrieved from cache which informs us whether or not the
			// value is JSON encoded or not (decoded accordingly), and then
			// _that_ value is cached for the second event.
			const valueType = valueTypes[paramType][paramId];

			newValue = (valueType === 'jsonpath')
				? value : JSON.parse(value);

			// Save the value for the annoying extra "valueType" event
			values[paramType][paramId] = newValue;
		} else if (key === 'valueType') {
			// Second event - Change of value type.  Cache the valueType
			// (which is `value` at this point) because it is not something
			// we are persisting in yaml.  The valueType change will be
			// followed by an immediate change of value from
			// `NodeConfigField`.
			valueTypes[paramType][paramId] = value;

			// Trigger the valueType change with the current value.
			newValue = trigger[paramType][paramId];
		}
		const type = (group) ? 'channelParameter' : 'triggerParameter';

		onChange && onChange({
			triggerId,
			triggerType,
			paramId,
			type,
			newValue
		});
	}

	onChangeInputParameter(paramId, key, value) {
		// The NodeConfigField on change fires two onChange events, as we don't
		// have valueType changes on flow inputs, we can simply continue.
		if (key === 'valueType') {
			return;
		}

		const { onChange, triggerId, triggerType } = this.props;

		onChange && onChange({
			triggerId,
			triggerType,
			paramId,
			type: 'inputParameter',
			newValue: value
		});
	}

	/**
	 * Extracts the validation errors for this particular trigger node.
	 *
	 * @return {Object} Returns an object if invalid, containing the validation
	 * errors.
	 */
	getValidationErrors() {
		const { validationMap, id } = this.props;

		// Extract the validation errors for that node
		const validationErrors = validationMap
			&& validationMap.nodeErrors
			&& validationMap.nodeErrors[id];

		return validationErrors;
	}

	renderTriggerTitle(validationErrors, isUnregisteredPlugin) {
		const { trigger, definition = {}, triggerId } = this.props;
		const {
			instanceName,
			status
		} = trigger;
		const {
			// icon default for unregistered plugins
			icon = 'icon-GG-optional'
		} = definition;

		const invalidParams = validationErrors && (
			!!validationErrors.paramsInvalid
			|| !!validationErrors.inputParamsInvalid
		);
		// Flags the title in error if the trigger is in that state or there
		// were validation errors.
		const invalid = !status.valid || invalidParams;

		return (
			<Title
				name={instanceName || triggerId}
				editable={!isUnregisteredPlugin}
				icon={icon}
				invalid={invalid}
				onChange={this.onChangeNodeName}
			/>
		);
	}

	renderTriggerId() {
		const { trigger, triggerId, newTriggerStatusMsg } = this.props;
		if (trigger.status.error
			&& trigger.status.error === newTriggerStatusMsg) {
			return null;
		}
		return (
			<Property>
				<Property.Name>
					{this.intl.formatMsg('Trigger ID')}
				</Property.Name>
				<Property.Value>
					<FormControl.Static className="nodeconfig-name">
						{triggerId}
					</FormControl.Static>
				</Property.Value>
			</Property>
		);
	}

	getFlowInputSelectors() {
		const { definition = {} } = this.props;
		const { requestSchema } = definition;

		// Parameters and flow inputs have different contexts and we want to
		// keep them separate. We want to prevent the situation where you could
		// add one parameter to the parameters' context and suddenly appearing
		// on the flow inputs selectors. That is why we redefine these selectors
		// here, rather than using the already existing ones from the props.
		const flowInputSelectors = [ '$.config', '$.env' ];

		// Add any additional properties that we can infer from the request
		// schema provided by the trigger plugins.
		jsonSchemaToJSONPath(requestSchema, flowInputSelectors, 'request');

		return flowInputSelectors;
	}

	render() {
		const {
			flowParameter,
			trigger,
			triggerType,
			definition = {},
			topic,
			selectors,
			onTabSelect
		} = this.props;

		const {
			flowInputsMap,
			triggerParameters,
			channelParameters,
			status
		} = trigger;

		const {
			name: typeName,
			channelFriendlyName,
			triggerParameters: parametersSchema,
			channelParameters: channelParametersSchema,
			requestSchema
		} = definition;

		// if the status is not valid, it is an error.  if it is disabled,
		// then it is disabled on disk, otherwise, enabled.
		const state = (!status.valid) ? 'error'
			: trigger.enabled ? 'enabled' : 'disabled';

		const isUnregisteredPlugin = !parametersSchema;

		// FIXME: It is possible for parameters to clash.  The parameters and
		// channel parameters cannot have the same key.  Currently, both
		// Solace and Kafka plugins do not have any clashing parameters but
		// it is something we need to solve, either here, or by ensuring all
		// plugin properties are unique.
		const inputParameters = {
			type: 'object',
			properties: {},
			required: [],
			additionalProperties: false
		};
		schemaToInputParameters(parametersSchema, inputParameters);

		// optional for plugins, but also channels for uninstalled plugins
		// do not have channelParametersSchema (can be undefined).
		schemaToInputParameters(
			channelParametersSchema,
			inputParameters,
			{
				group: channelFriendlyName
			}
		);

		let onEnable;
		let onDelete;
		let onChange;
		let onInputEnable;
		let onInputChange;
		if (!isUnregisteredPlugin) {
			// permit edits
			onEnable = this.onEnableParameter;
			onDelete = this.onDeleteParameter;
			onChange = this.onChangeParameter;
			onInputEnable = this.onEnableInputParameter;
			onInputChange = this.onChangeInputParameter;
		} else {
			// the plugin is uninstalled and we may have parameter values
			// for trigger/channel, but no corresponding schema.  At this
			// point, inputParameters would have values that were
			// converted from schema to flow-node compatible
			// `inputParameter`
			const input = inputParameters.properties;
			for (const paramId in triggerParameters) {
				input[paramId] = {
					type: 'string',
					description: ''
				};
			}
			if (channelParameters) {
				for (const paramId in channelParameters) {
					input[paramId] = {
						type: 'string',
						description: ''
					};
				}
			}
		}

		// FIXME: It is possible for parameters to clash.  The parameters and
		// channel parameters cannot have the same key.  Currently, both
		// Solace and Kafka plugins do not have any clashing parameters but
		// it is something we need to solve, either here, or by ensuring all
		// plugin properties are unique.
		const flowParameters = [];
		mapToFlowParameters(
			triggerParameters,
			this.parameters.valueTypes.triggerParameters,
			flowParameters
		);
		mapToFlowParameters(
			channelParameters,
			this.parameters.valueTypes.channelParameters,
			flowParameters
		);

		const flowInputsDescription = requestSchema
			? this.intl.formatMsg('These are the values that are assigned as inputs to this flow. The trigger\'s Outputs are used as flow inputs to pass data into the flow.', 'input_values_assigned_with_outputs')
			: this.intl.formatMsg('These are the values that are assigned as inputs to this flow.', 'input_values_assigned');

		const validationErrors = this.getValidationErrors();

		const title = this.renderTriggerTitle(validationErrors, isUnregisteredPlugin);

		const flowInputSelectors = this.getFlowInputSelectors();

		return (
			<div className="nodeconfig">
				<Form horizontal className="nodeconfig-form">
					<Panel
						key="panel-node"
						header={title}
						className="panel-node"
					>
						<Property>
							<Property.Name>
								{this.intl.formatMsg('Trigger')}
							</Property.Name>
							<Property.Value>
								<FormControl.Static className="nodeconfig-name">
									{typeName || triggerType}
								</FormControl.Static>
							</Property.Value>
						</Property>
						{this.renderTriggerId()}
						{this.renderChannel()}
						<Property>
							<Property.Name>
								{this.intl.formatMsg('Status')}
							</Property.Name>
							<Property.Value>
								<FormControl.Static className="nodeconfig-name">
									<Status
										tooltipPlacement="bottom"
										// enabled | disabled | error
										type={state}
										tooltip={status.error}
									/>
								</FormControl.Static>
							</Property.Value>
						</Property>
						{this.renderDescription()}
					</Panel>
					<Panel
						key="panel-config"
						className="panel-config"
					>
						<Tabs
							id="config-tabs"
							className="axui tabs"
							animation={false}
							activeKey={topic}
							onSelect={onTabSelect}
						>
							{renderParametersTab({
								title: this.intl.formatMsg('Parameters'),
								inputParameters,
								flowParameters,
								selectors,
								onEnable,
								onDelete,
								onChange,
								emptyMessage: this.intl.formatMsg('No configurable parameters.'),
								errors: validationErrors
							})}
							{renderTriggerOutputsTab({
								title: this.intl.formatMsg('Outputs'),
								description: this.intl.formatMsg('When this trigger executes, it will output the data below, which can be used to provide Flow inputs.'),
								schema: requestSchema,
								emptyMessage: this.intl.formatMsg('No outputs.')
							})}
							{renderTriggerInputsTab({
								title: this.intl.formatMsg('Flow inputs'),
								description: flowInputsDescription,
								inputs: flowInputsMap,
								parameter: flowParameter,
								selectors: flowInputSelectors,
								onChange: onInputChange,
								onEnable: onInputEnable,
								emptyMessage: this.intl.formatMsg('No configurable Flow inputs.'),
								errors: validationErrors
							})}
						</Tabs>
					</Panel>
				</Form>
			</div>
		);
	}
}

TriggerConfig.propTypes = {
	/**
	 * The node ID. It has this format:
	 * 		$$__axway__trigger_<TYPE>_<INSTANCE-NAME>
	 *
	 * Example: $$__axway__trigger_timer_my-interval
	 */
	id: PropTypes.string.isRequired,
	/**
	 * The flow input parameter (i.e. flow.parameter).
	 */
	flowParameter: PropTypes.object,
	/**
	 * The flow-trigger ID.
	 */
	triggerId: PropTypes.string.isRequired,
	/**
	 * The flow-trigger type.
	 */
	triggerType: PropTypes.string.isRequired,
	/**
	 * The status message for newly created triggers.
	 */
	newTriggerStatusMsg: PropTypes.string,
	/**
	 * The flow-trigger (see TM.addTriggerInfo).
	 */
	trigger: PropTypes.object.isRequired,
	/**
	 * The flow-trigger plugin definition.  Not required for
	 * unregistered plugins (see TM.addDefinitionInfo).
	 */
	definition: PropTypes.object,
	/**
	 * The topic to render.
	 */
	topic: FlowPropTypes.topic.isRequired,
	/**
	 * A list of available selectors.
	 */
	selectors: PropTypes.arrayOf(PropTypes.string).isRequired,
	/**
	 * Callback for when a parameter is enabled.
	 */
	onParamEnable: PropTypes.func,
	/**
	 * A flow validation status object.
	 *
	 * Example:
	 * validationMap = {
	 * 		valid: false,
	 * 		nodeErrors: {
	 * 			node-id: {
	 * 				errorType: { ... }
	 * 			}
	 * 		}
	 * }
	 */
	validationMap: PropTypes.shape({
		valid: PropTypes.bool,
		nodeErrors: PropTypes.object
	}),
	/**
	 * Callback for when a tab is selected, and a topic is to be changed.
	 */
	onTabSelect: PropTypes.func,
	/**
	 * Callback for when the trigger config is changed.
	 */
	onChange: PropTypes.func
};

export default injectIntl(TriggerConfig);
